/**
 * src/state/networkRetryQueue.js
 *
 * The missing half of the per-record write contract (GH #5242, #5247, #5253).
 *
 * `usePersistence.js` documents, and implements, a network-retry contract for
 * the tenant_state blob: a write that fails because the connection dropped is
 * kept, retried on a backing-off schedule, and retried immediately when the
 * browser reports connectivity back. Nothing equivalent existed for the
 * per-record stores that ADR-0002/0004/0005 moved collections into —
 * `assets-write` (assets, BIA) and `controls-write` (SoA, ownership,
 * effectiveness). Those keys are STRIPPED from the blob by `state-write`, so
 * the blob's retry machinery cannot rescue them even in principle: the
 * debounced save still succeeds, the server drops the key, and nothing
 * anywhere ever tries again.
 *
 * The result was that "the network blinked" and "the server refused this"
 * produced the same outcome — the record was rolled back out of the list, or
 * left optimistically on screen, and never written. A DR-test result, an
 * asset, a control's applicability decision: gone, with the UI having moved on.
 *
 * What this queue is, and is not
 * ------------------------------
 * It retries TRANSPORT failures only — writes that never reached the server
 * (`writeErrors.isNetworkFailure`). A refusal (any response with a status) is
 * never queued: re-sending it unchanged would be refused identically forever,
 * and for a create it could mint a duplicate. Refusals stay the caller's
 * problem, handled the way they are today (roll back, surface inline).
 *
 * De-duped by key, so repeated edits to one record collapse to the newest
 * value rather than replaying a stale one behind it. Bounded: after
 * MAX_ATTEMPTS the entry is handed back to the caller via `onGiveUp` so the
 * optimistic record can finally be rolled back and the user told plainly —
 * an unacknowledged write is never reported as saved (STG-B-003).
 *
 * `onGiveUp` is OPTIONAL but giving up is not silent. Every original caller
 * omitted it, so the "told plainly" half of the contract above was documented
 * and never delivered: a queued write that ran out of attempts vanished with
 * no banner, no message, and — for callers that had told the user "this will
 * be saved when the connection returns" — a promise nothing ever kept
 * (#5356). A caller with a better surface (an inline error in the dialog that
 * owns the record) passes `onGiveUp`; everything else gets the app-shell
 * write-error banner by default.
 *
 * Modelled on `riskWriteOutbox.js`, which that module's own header nominates as
 * the pattern for exactly this. It is deliberately smaller: risk writes need
 * create-vs-edit 409 handling (ID-GEN-003) because they carry minted ids; this
 * queue never retries a 409 at all, so it needs none of that machinery.
 */

import { isNetworkFailure } from '../api/writeErrors.js';

// Backoff between automatic attempts (ms), indexed by attempts already made.
// The last value is the ceiling. Reconnect collapses this — an 'online' event
// is far better evidence that the next attempt can succeed than any timer.
//
// The first step is deliberately short. Reconnect handling below is a best
// effort, not a guarantee: 'online' is not delivered reliably in every browser
// or automation environment, and when it is missed the ladder is the only
// thing left. A first step measured in seconds meant a write made during a
// blink of connectivity could still be sitting in the queue when the user
// navigated away, which discards it (#5356).
const BACKOFF_MS = [1_000, 5_000, 15_000, 30_000, 60_000];
// Attempts (including the caller's original) before we stop trying on our own
// and hand the failure back. Chosen so an ordinary tunnel/lift outage is
// ridden out, while a genuinely dead connection surfaces within ~2 minutes
// rather than leaving the user staring at "will retry" indefinitely.
const MAX_ATTEMPTS = 5;

// How often connectivity is re-checked directly while anything is queued.
//
// #5658: the reconnect signal was the 'online' event alone, which the note on
// bindReplayTriggers already admits is not delivered by every browser — and is
// not delivered at all by the network-emulation layers used to test this. When
// it is missed the backoff ladder is the only thing left, and past the first
// step that ladder is measured in seconds: a write made offline, retried once
// at 1s and then not again until 5s, was still sitting in the queue when the
// user navigated — which discards it, because this queue lives only in memory.
// That is how "queue and resend after reconnect" turned into a GDPR Article 30
// RoPA entry that was never written and never reported (TRYGGR_INTENT.md §5).
//
// navigator.onLine is always readable, so polling it turns the reliable half
// of the signal into the primary one. A second is short enough that a
// reconnect is acted on well inside the window a user takes to navigate away,
// and the poll only runs while something is actually waiting.
const ONLINE_POLL_MS = 1_000;

/** key -> { key, attempt, onSuccess, onGiveUp, attempts, timer } */
const entries = new Map();
let onlineBound = false;
let onlinePollTimer = null;
let wasOffline = false;

/** True when the browser positively says it is offline right now. */
function browserOffline() {
  try {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  } catch {
    return false;
  }
}

// Watch navigator.onLine directly for as long as anything is queued, so a
// reconnect the 'online' event never reported still collapses the backoff.
function startOnlinePolling() {
  if (onlinePollTimer || typeof setInterval !== 'function') return;
  wasOffline = browserOffline();
  onlinePollTimer = setInterval(() => {
    if (!entries.size) {
      stopOnlinePolling();
      return;
    }
    const offlineNow = browserOffline();
    // Only the transition counts. Replaying on every tick while online would
    // ignore the backoff entirely and hammer a server that is simply slow.
    if (wasOffline && !offlineNow) replayAll();
    wasOffline = offlineNow;
  }, ONLINE_POLL_MS);
}

function stopOnlinePolling() {
  if (!onlinePollTimer) return;
  clearInterval(onlinePollTimer);
  onlinePollTimer = null;
  wasOffline = false;
}

/**
 * Raise the app-shell write-error banner. Used when a caller supplied no
 * `onGiveUp` of its own — see the give-up path in `run()` for why silence is
 * not an acceptable default here.
 */
function announceGaveUp() {
  try {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('seasuite:write-error'));
    }
  } catch {
    /* non-browser — no-op */
  }
}

function bindReplayTriggers() {
  if (onlineBound || typeof window === 'undefined' || !window.addEventListener) return;
  onlineBound = true;
  window.addEventListener('online', () => replayAll());
  // 'online' alone is not enough. It is not fired reliably by every browser
  // (and not at all by some automation/network-emulation layers), and a laptop
  // waking from sleep can regain connectivity without one. A user returning to
  // the tab is independent, always-delivered evidence that this is a good
  // moment to try again — and it is the moment they are most likely to be
  // about to navigate away and lose the queued write (#5356).
  window.addEventListener('focus', () => replayAll(false));
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') replayAll(false);
    });
  }
}

function clearTimer(entry) {
  if (entry.timer) {
    clearTimeout(entry.timer);
    entry.timer = null;
  }
}

// Indexed by retries THIS queue has already made — the caller's own failed
// call is attempt 1 but is not one of ours, so the first queued retry waits
// BACKOFF_MS[0].
function backoffFor(retriesMade) {
  const i = Math.min(Math.max(retriesMade, 0), BACKOFF_MS.length - 1);
  return BACKOFF_MS[i];
}

function schedule(entry) {
  clearTimer(entry);
  entry.timer = setTimeout(
    () => {
      entry.timer = null;
      run(entry);
    },
    backoffFor(entry.attempts - 1)
  );
}

function finish(entry) {
  clearTimer(entry);
  // Only drop the entry if it is still the current one for this key — a newer
  // edit may have replaced it while this attempt was in flight.
  if (entries.get(entry.key) === entry) entries.delete(entry.key);
  if (!entries.size) stopOnlinePolling();
}

function run(entry) {
  // Superseded by a newer edit to the same record — that entry owns the key now.
  if (entries.get(entry.key) !== entry) return;
  entry.attempts += 1;
  Promise.resolve()
    .then(() => entry.attempt())
    .then((result) => {
      if (entries.get(entry.key) !== entry) return;
      finish(entry);
      try {
        entry.onSuccess?.(result);
      } catch (e) {
        console.warn('[networkRetryQueue] onSuccess threw:', e);
      }
    })
    .catch((err) => {
      if (entries.get(entry.key) !== entry) return;
      // The server answered this time — it is a refusal, not a transport
      // failure, and retrying it is not our job. Hand it back immediately.
      if (!isNetworkFailure(err) || entry.attempts >= MAX_ATTEMPTS) {
        finish(entry);
        // Giving up is the moment the write stops being "pending" and becomes
        // "lost", so it is the one moment the user MUST hear about. Every
        // caller had already rolled its record back, so nothing on screen
        // claimed to be saved — but nothing said the retry had been abandoned
        // either, and a caller that told the user "it will be saved when the
        // connection returns" was left standing when it never was (STG-B-003,
        // #5356). A caller with a better surface overrides this; the default
        // is never silence.
        try {
          if (entry.onGiveUp) entry.onGiveUp(err);
          else announceGaveUp();
        } catch (e) {
          console.warn('[networkRetryQueue] onGiveUp threw:', e);
        }
        return;
      }
      schedule(entry);
    });
}

/**
 * Queue a per-record write that failed in transport.
 *
 * Returns false — and queues nothing — when `err` is a refusal rather than a
 * transport failure, so callers can use the return value to decide between
 * "roll back and show the error" and "keep the optimistic record, tell the
 * user it is pending".
 *
 * @param {object} spec
 * @param {string} spec.key        stable per-record key, e.g. `asset:<id>`
 * @param {() => Promise<any>} spec.attempt  re-issues the same write
 * @param {unknown} spec.error     the failure that prompted queuing
 * @param {(result: any) => void} [spec.onSuccess]  called once it lands
 * @param {(err: unknown) => void} [spec.onGiveUp]  called when we stop trying;
 *        when omitted the app-shell write-error banner is raised instead
 * @returns {boolean} true when the write was queued for retry
 */
export function queueNetworkWrite({ key, attempt, error, onSuccess, onGiveUp }) {
  if (!key || typeof attempt !== 'function') return false;
  if (!isNetworkFailure(error)) return false;

  bindReplayTriggers();
  const existing = entries.get(key);
  if (existing) clearTimer(existing);

  const entry = {
    key,
    attempt,
    onSuccess,
    onGiveUp,
    // The caller's own failed call counts as attempt 1.
    attempts: 1,
    timer: null,
  };
  entries.set(key, entry);
  schedule(entry);
  startOnlinePolling();
  return true;
}

/**
 * Retry every queued write now.
 *
 * `resetBudget` distinguishes the two reasons we replay early, and the
 * distinction matters because the attempt budget is the only thing that ever
 * ends a doomed write:
 *
 *  • An 'online' event is positive evidence that connectivity CHANGED, so it
 *    resets the budget — an outage longer than MAX_ATTEMPTS backoffs must not
 *    permanently disqualify a write the network can now carry.
 *  • Focus and visibility say only that the user came back. They are worth
 *    retrying on (see bindReplayTriggers) but they are not evidence of
 *    anything having changed, and resetting the budget on them would let a
 *    user idly switching tabs keep a permanently-failing write alive forever —
 *    which never reaches give-up, so they are never told it failed.
 */
export function replayAll(resetBudget = true) {
  for (const entry of [...entries.values()]) {
    clearTimer(entry);
    if (resetBudget) entry.attempts = 0;
    run(entry);
  }
}

/** True while any per-record write is waiting to be retried. */
export function hasPendingNetworkWrites() {
  return entries.size > 0;
}

/** Keys of every write still waiting, for UI that wants to name them. */
export function getPendingNetworkWriteKeys() {
  return [...entries.keys()];
}

/**
 * Drop a queued write for `key` without running it, if one is waiting.
 *
 * For a caller whose failed writes are queued here but whose SUCCESSFUL
 * writes bypass the queue entirely (the ordinary case — this queue only ever
 * sees the transport-failure path), a queued retry can outlive the edit it
 * was carrying: the record fails, gets queued with that snapshot, and a
 * later, newer ordinary write for the same key succeeds on its own. Nothing
 * about that success touches this queue, so the stale entry is still there,
 * still armed, and will eventually replay a snapshot older than what the
 * server already holds — for a same-key action=save write with no version
 * check, silently overwriting the newer data with the stale one, or (if it
 * gives up instead) reporting the edit as lost when it was not.
 *
 * A caller that is about to issue a fresh ordinary write for a key should
 * call this first, so a stale queued retry can never resolve after it.
 */
export function dropNetworkWrite(key) {
  const entry = entries.get(key);
  if (!entry) return;
  clearTimer(entry);
  entries.delete(key);
  if (!entries.size) stopOnlinePolling();
}

/** Test hook — drops every queued write without running it. */
export function resetNetworkRetryQueue() {
  for (const entry of entries.values()) clearTimer(entry);
  entries.clear();
  stopOnlinePolling();
}
